// Out → Returned, as a PAGE in the rental shell (design:
// backend/docs/DESIGN_CHECKIN_CHECKOUT_PAGES.md §1.2). The former ReturnStep
// sheet, re-housed: condition, late line, notes, damage charge and a photo
// big enough to review on the left; hour meters, spec updates and the
// close-out card on the right (stacked on a phone). The close-out card has
// two phases in the same container: PROJECTED before Finish (computed here
// for display only) and ACTUAL after Finish (the server's numbers, with the
// inline Collect / Refund). Nothing is written until Finish; "← Back to
// order" asks before discarding anything typed. The transition payload and
// the calls around it are exactly the step's (photo upload and spec PATCHes
// before the POST, hour meters after — BACKLOG 24).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../../lib/axiosClient';
import { UI, fmtMoney, useIsNarrow, isTouch, STATUS_LABEL, STATUS_COLOR } from './pipeline';

const CONDITIONS = [
  { id: 'good',          label: '✅ Good',          desc: 'All items returned as expected', color: '#4ade80', bg: '#14532d' },
  { id: 'damaged',       label: '⚠️ Damaged',       desc: 'One or more items damaged',     color: '#f59e0b', bg: '#451a03' },
  { id: 'missing_parts', label: '❌ Missing Parts', desc: 'Items returned incomplete',     color: '#f87171', bg: '#3b1a1a' },
];

const fmtD = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
const r2 = (n) => Math.round(Number(n || 0) * 100) / 100;
const lineLabel = (k) => k === 'damage' ? 'Damage' : k === 'late_fee' ? 'Late fee' : k;

export default function ReturnPage({ order, onBack, transition, onDone, inventoryItems = [], dbCategories = [], openCollect, openRefund, onRefresh }) {
  const [done, setDone] = useState(null);   // { order, close_out } after Finish — the ACTUAL close-out phase
  const narrow = useIsNarrow();             // ≤ 640: one column, fixed footer
  const [condition, setCondition] = useState('good');
  const [notes, setNotes] = useState('');
  const [damageCharge, setDamageCharge] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoFull, setPhotoFull] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [showSpecUpdate, setShowSpecUpdate] = useState(false);
  const [hourMeters, setHourMeters] = useState({});
  const [specUpdates, setSpecUpdates] = useState({});
  const [late, setLate] = useState(null);      // late-return-preview
  const fileRef = useRef();
  const rootRef = useRef(null);
  const closeoutRef = useRef(null);

  // A page starts at the top, whatever the list row scrolled before it.
  useEffect(() => {
    try { window.scrollTo(0, 0); } catch {}
    for (let el = rootRef.current?.parentElement; el; el = el.parentElement) if (el.scrollTop) el.scrollTop = 0;
  }, [order.id]);

  const [internalCategories, setInternalCategories] = useState([]);
  useEffect(() => {
    axios.get('/api/rentals/categories').then(r => setInternalCategories(r.data.categories || [])).catch(() => {});
    axios.get(`/api/rentals/orders/${order.id}/late-return-preview`).then(r => setLate(r.data)).catch(() => setLate({ error: true }));
  }, [order.id]);
  const resolvedCategories = internalCategories.length > 0 ? internalCategories : dbCategories;

  const getItemSchema = (orderItem) => {
    const invItem = inventoryItems.find(i => i.id === orderItem.rental_item_id);
    if (!invItem?.category_id) return [];
    const cat = resolvedCategories.find(c => c.id === invItem.category_id);
    return cat?.custom_field_schema || [];
  };
  const setSpecVal = (itemId, key, val) => setSpecUpdates(prev => ({ ...prev, [itemId]: { ...(prev[itemId] || {}), [key]: val } }));

  function handlePhoto(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = ev => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  }

  // What the payload will carry: the charge only counts on a non-good return (the input is only shown then).
  const damage = condition !== 'good' ? r2(Number(damageCharge) || 0) : 0;

  async function handleReturn() {
    setSaving(true); setErr(null);
    let photoUrl = '';
    if (photoFile && condition !== 'good') {
      setUploading(true);
      try {
        const reader = new FileReader();
        const base64 = await new Promise((res, rej) => { reader.onload = e => res(e.target.result.split(',')[1]); reader.onerror = rej; reader.readAsDataURL(photoFile); });
        const r = await axios.post(`/api/rentals/orders/${order.id}/damage-photo`, { photo_base64: base64, file_type: photoFile.type });
        photoUrl = r.data.url || '';
      } catch (e) { console.error('Photo upload failed', e); }
      setUploading(false);
    }
    const specEntries = Object.entries(specUpdates);
    if (specEntries.length > 0) {
      await Promise.all(specEntries.map(async ([itemId, updates]) => {
        try { await axios.patch(`/api/rentals/items/${itemId}`, { custom_data: updates }); } catch (e) { console.error('Failed to update item specs', e); }
      }));
    }
    const r = await transition(order, { action: 'return', return_condition: condition, return_notes: notes, damage_charge: damage, damage_photo_url: photoUrl });
    if (!r.ok) { setErr(r.reason); setSaving(false); return; }
    const hm = Object.entries(hourMeters || {}).filter(([, h]) => h !== '' && h != null);
    if (hm.length) {
      await Promise.all(hm.map(([variantId, hours]) => axios.patch(`/api/rentals/products/variants/${variantId}`, { hour_meter: Number(hours) }).catch(e => console.error('Failed to update hour meter', e))));
    }
    setSaving(false);
    // Stay on the page: the close-out card flips to what the return changed money-wise, from the numbers the server just wrote.
    setDone({ order: r.order, close_out: r.close_out });
    // Lists and the detail behind the page learn the return now; the order prop follows.
    if (onRefresh) onRefresh();
  }
  // The host refreshes `order` after an inline collect / refund — keep the close-out in step with it.
  useEffect(() => { if (done && order && order.id === done.order.id && order.payment) setDone(d => ({ ...d, order })); }, [order]); // eslint-disable-line react-hooks/exhaustive-deps
  // After Finish on a phone the close-out card is below the fold: bring it into view.
  useEffect(() => { if (done && closeoutRef.current) { try { closeoutRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch {} } }, [done]);

  const dirty = condition !== 'good' || !!notes.trim() || !!damageCharge || !!photoFile
    || Object.values(hourMeters).some(v => v !== '' && v != null) || Object.keys(specUpdates).length > 0;
  function back() {
    if (done) return onDone(done.order);                       // the return is written; leaving = Done
    if (dirty && !window.confirm('Discard what you entered?')) return;
    onBack();
  }

  const sel = CONDITIONS.find(c => c.id === condition);
  const overdue = !!order.next_action?.is_overdue;
  const na = order.next_action || {};
  const sc = STATUS_COLOR[done ? done.order.status : order.status] || STATUS_COLOR.started;
  const units = (order.rental_order_items || []).filter(i => i.variant_id);
  const locked = !!done;   // read-only after Finish

  const lateLine = () => {
    if (!late) return <div style={{ fontSize: 12, color: '#94a3b8' }}>Checking the late-return policy…</div>;
    if (late.error) return <div style={{ fontSize: 12, color: '#f87171' }}>Could not read the late-return policy.</div>;
    const already = Number(late.current_late_fee || 0);
    if (!late.policy_enabled) return <div style={{ fontSize: 12, color: '#94a3b8' }}>{late.days_late > 0 ? `${late.days_late} day${late.days_late === 1 ? '' : 's'} past due — ` : ''}late-return fees are off in Settings. Nothing is charged.</div>;
    if (late.chargeable_days === 0) return <div style={{ fontSize: 12, color: '#94a3b8' }}>{late.days_late > 0 ? `${late.days_late} day${late.days_late === 1 ? '' : 's'} past due, within the grace period — no late fee.` : 'Returned on time — no late fee.'}</div>;
    const delta = r2(Number(late.fee) - already);
    return (
      <div data-testid="late-line" style={{ fontSize: 12, color: '#fbbf24', lineHeight: 1.6 }}>
        <div><b>Late return:</b> {late.chargeable_days} chargeable day{late.chargeable_days === 1 ? '' : 's'} ({late.days_late} past due) → <b>{fmtMoney(late.fee)}</b>{late.capped ? ' (capped)' : ''} per policy</div>
        <div style={{ color: '#94a3b8' }}>{already > 0 ? `already accrued ${fmtMoney(already)} → adds ${fmtMoney(delta)}` : `adds ${fmtMoney(delta)} to the total`}</div>
      </div>
    );
  };

  // PROJECTED close-out (display only): what the total will read once the late fee and the typed damage land.
  const projected = useMemo(() => {
    const lateDelta = late && !late.error && late.policy_enabled && Number(late.chargeable_days) > 0 ? r2(Number(late.fee) - Number(late.current_late_fee || 0)) : 0;
    const total = r2(order.total), paid = r2(order.amount_paid);
    const net = r2(total + lateDelta + damage - paid);
    return { total, paid, lateDelta, damage, net };
  }, [late, order.total, order.amount_paid, damage]);

  const card = (title, body) => (
    <div style={{ border: '1px solid #2a3050', borderRadius: 10, overflow: 'hidden', background: '#252838' }}>
      <div style={{ padding: '12px 16px', background: '#1e2235', fontWeight: 700, fontSize: 14, color: '#f1f5f9' }}>{title}</div>
      <div style={{ padding: 16 }}>{body}</div>
    </div>
  );

  const closeoutCard = () => {
    if (!done) {
      const p = projected;
      return (
        <div ref={closeoutRef} data-testid="return-closeout" data-phase="projected" style={{ border: '1px solid #2a3050', borderRadius: 10, overflow: 'hidden', background: '#252838' }}>
          <div style={{ padding: '12px 16px', background: '#1e2235', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#f1f5f9' }}>Close-out</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5 }}>projected</span>
          </div>
          <div style={{ padding: 16 }}>
            <div style={UI.row}><span>Total now</span><span style={{ color: '#f1f5f9' }}>{fmtMoney(p.total)}</span></div>
            {p.lateDelta > 0 && <div data-testid="projected-late" style={UI.row}><span>+ Late fee</span><span style={{ color: '#fbbf24' }}>+{fmtMoney(p.lateDelta)}</span></div>}
            {p.damage > 0 && <div data-testid="projected-damage" style={UI.row}><span>+ Damage</span><span style={{ color: '#fbbf24' }}>+{fmtMoney(p.damage)}</span></div>}
            <div style={UI.row}><span>Paid</span><span style={{ color: '#4ade80' }}>{fmtMoney(p.paid)}</span></div>
            {p.net > 0 && <div data-testid="projected-due" style={{ ...UI.row, fontWeight: 800, color: '#f87171', fontSize: 14, borderBottom: 'none' }}><span>To collect</span><span>{fmtMoney(p.net)}</span></div>}
            {p.net < 0 && <div data-testid="projected-owed" style={{ ...UI.row, fontWeight: 800, color: '#a78bfa', fontSize: 14, borderBottom: 'none' }}><span>Client is owed</span><span>{fmtMoney(-p.net)}</span></div>}
            {p.net === 0 && <div data-testid="projected-settled" style={{ ...UI.row, fontWeight: 700, color: '#4ade80', borderBottom: 'none' }}><span>✓ Settled</span><span>{fmtMoney(p.paid)} of {fmtMoney(p.total + p.lateDelta + p.damage)}</span></div>}
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>{p.net > 0 ? 'Collect appears here after Finish.' : p.net < 0 ? 'Refund appears here after Finish.' : 'The server settles the numbers on Finish.'}</div>
          </div>
        </div>
      );
    }
    const o = done.order; const pay = o.payment || {}; const co = done.close_out || {};
    const due = Number(pay.due_from_client ?? co.due_from_client ?? 0), owedNow = Number(pay.owed_to_client ?? co.owed_to_client ?? 0);
    const lines = co.lines || [];
    return (
      <div ref={closeoutRef} data-testid="return-closeout" data-phase="actual" style={{ border: '1px solid #1e4a35', borderRadius: 10, overflow: 'hidden', background: '#252838' }}>
        <div style={{ padding: '12px 16px', background: '#0c1a14', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#4ade80' }}>✓ Returned {o.order_number || '#' + o.id}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .5 }}>close-out</span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{o.client_name} · equipment is back and the units are free</div>
          {lines.map((l, i) => <div key={i} style={UI.row}><span>{lineLabel(l.kind)} added</span><span style={{ color: '#fbbf24' }}>+{fmtMoney(l.amount)}</span></div>)}
          <div style={UI.row}><span>Total</span><span style={{ fontWeight: 700, color: '#f1f5f9' }}>{fmtMoney(o.total)}</span></div>
          <div style={UI.row}><span>Paid</span><span style={{ color: '#4ade80' }}>{fmtMoney(o.amount_paid)}</span></div>
          {due > 0 && (
            <>
              <div data-testid="closeout-due" style={{ ...UI.row, fontWeight: 800, color: '#f87171', fontSize: 14 }}><span>{lines.length ? `${lines.map(l => lineLabel(l.kind)).join(' + ')} adds ${fmtMoney(lines.reduce((s, l) => s + Number(l.amount), 0))} —` : ''} to collect</span><span>{fmtMoney(due)}</span></div>
              {openCollect && <button type="button" data-testid="closeout-collect" onClick={() => openCollect(o, { defaultAmount: due })} style={{ ...UI.goBtn(true), width: '100%', marginTop: 8, padding: 14 }}>💰 Collect {fmtMoney(due)} now</button>}
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Or later: the order's primary button reads "Collect {fmtMoney(due)}" until it is settled.</div>
            </>
          )}
          {owedNow > 0 && (
            <>
              <div data-testid="closeout-owed" style={{ ...UI.row, fontWeight: 800, color: '#a78bfa', fontSize: 14 }}><span>Client is owed</span><span>{fmtMoney(owedNow)}</span></div>
              {openRefund && <button type="button" data-testid="closeout-refund" onClick={() => openRefund(o)} style={{ ...UI.goBtn(true, '#4c1d95', '#e9d5ff'), width: '100%', marginTop: 8, padding: 14 }}>↩ Refund {fmtMoney(owedNow)}…</button>}
            </>
          )}
          {due <= 0 && owedNow <= 0 && <div data-testid="closeout-settled" style={{ ...UI.row, color: '#4ade80', fontWeight: 700 }}><span>✓ Nothing to collect — paid in full</span><span>{fmtMoney(o.amount_paid)} of {fmtMoney(o.total)}</span></div>}
        </div>
      </div>
    );
  };

  const canFinish = !locked && !saving && !uploading && !(condition !== 'good' && !notes.trim());
  const finishReason = locked ? null : condition !== 'good' && !notes.trim() ? 'Add a note on the condition' : null;
  const gridCols = narrow ? '1fr' : 'minmax(360px,1.2fr) minmax(320px,1fr)';
  const inp = { ...UI.inp, opacity: locked ? 0.6 : 1 };

  return (
    <div ref={rootRef} data-testid="return-step" data-page="return" style={{ maxWidth: 1400, margin: '0 auto', color: '#e2e8f0', paddingBottom: narrow ? 110 : 0, display: 'flex', flexDirection: 'column', minHeight: narrow ? undefined : 'calc(100vh - 56px)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <button type="button" data-testid="page-back" onClick={back} style={{ background: 'none', border: 'none', color: '#f97316', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: 0 }}>← Back to order</button>
        <span style={{ color: '#333' }}>|</span>
        <span style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{done ? '✓ Returned' : '↩ Return'} {order.order_number || '#' + order.id}</span>
        <span data-testid="order-status-badge" style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: sc.bg, color: sc.color, border: `1px solid ${sc.border}` }}>{STATUS_LABEL[done ? done.order.status : order.status] || order.status}</span>
        {overdue && !done && <span data-testid="overdue-badge" style={{ fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: '#3a1a1a', color: '#f87171', border: '1px solid #4a2020' }}>⚠️ Overdue</span>}
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{order.client_name} · due {fmtD(order.to_date)} · {(order.rental_order_items || []).length} item{(order.rental_order_items || []).length === 1 ? '' : 's'}</span>
      </div>

      {/* Two columns on a desk, one on a phone */}
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: narrow ? 12 : 20, alignItems: 'start' }}>
        {/* LEFT: what came back */}
        <div data-testid="return-col-condition" style={{ display: 'flex', flexDirection: 'column', gap: narrow ? 12 : 20, opacity: locked ? 0.55 : 1 }}>
          {card('Equipment condition', (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {CONDITIONS.map(c => (
                  <button type="button" key={c.id} data-testid={`condition-${c.id}`} onClick={() => setCondition(c.id)} disabled={locked}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', minHeight: 56, boxSizing: 'border-box', borderRadius: 10, border: `1px solid ${condition === c.id ? c.color : '#2e3150'}`, background: condition === c.id ? `${c.color}15` : '#181928', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${condition === c.id ? c.color : '#94a3b8'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {condition === c.id && <div style={{ width: 10, height: 10, borderRadius: '50%', background: c.color }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: condition === c.id ? c.color : '#e2e8f0' }}>{c.label}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.desc}</div>
                    </div>
                  </button>
                ))}
              </div>

              <label style={UI.lbl}>Late return</label>
              {lateLine()}

              <label style={UI.lbl}>Return notes {condition !== 'good' && <span style={{ color: '#f87171' }}>*</span>}</label>
              <textarea style={{ ...inp, height: 70, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} disabled={locked}
                placeholder={condition === 'good' ? 'Any notes on the return… (optional)' : 'Describe the damage or missing items…'} />

              {condition !== 'good' && (<>
                <label style={UI.lbl}>Damage / loss charge ($)</label>
                <input data-testid="damage-charge" style={inp} type="number" min="0" step="0.01" inputMode="decimal" value={damageCharge} onChange={e => setDamageCharge(e.target.value)} disabled={locked} placeholder="0.00 — amount to charge the client" />
                <label style={UI.lbl}>Photo evidence <span style={{ color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                <input ref={fileRef} data-testid="damage-photo-input" type="file" accept="image/*" {...((narrow || isTouch()) ? { capture: 'environment' } : {})} style={{ display: 'none' }} onChange={handlePhoto} />
                {photoPreview ? (
                  <div style={{ position: 'relative', background: '#0f1017', borderRadius: 8, border: '1px solid #2a3050', overflow: 'hidden' }}>
                    <img data-testid="damage-photo-preview" src={photoPreview} alt="damage" onClick={() => setPhotoFull(true)} title="Open full size"
                      style={{ display: 'block', width: '100%', maxHeight: narrow ? '55vh' : 420, objectFit: 'contain', cursor: 'zoom-in' }} />
                    {!locked && <button type="button" onClick={() => { setPhotoFile(null); setPhotoPreview(''); }} aria-label="Remove photo" style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', border: 'none', color: '#f87171', borderRadius: 20, width: 28, height: 28, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>✕</button>}
                  </div>
                ) : (
                  <button type="button" data-testid="damage-photo-button" onClick={() => fileRef.current?.click()} disabled={locked} style={{ width: '100%', padding: 16, borderRadius: 8, border: '1px dashed #2a3050', background: '#181928', color: '#e2e8f0', cursor: 'pointer', fontSize: 14 }}>
                    📷 {narrow || isTouch() ? 'Take a photo' : 'Attach a photo'}
                  </button>
                )}
              </>)}
            </>
          ))}
        </div>

        {/* RIGHT: the units and the money */}
        <div data-testid="return-col-units" style={{ display: 'flex', flexDirection: 'column', gap: narrow ? 12 : 20 }}>
          {(units.length > 0 || (order.rental_order_items || []).length > 0) && (
            <div style={{ opacity: locked ? 0.55 : 1 }}>
              {card('Units', (
                <>
                  {units.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {units.map(i => (
                        <div key={i.variant_id} data-testid="unit-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', minHeight: 48, boxSizing: 'border-box', background: '#181928', border: '1px solid #1a1d2e', borderRadius: 8 }}>
                          <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#e2e8f0' }}><div style={{ fontWeight: 600 }}>{i.item_name}</div>{i.variant_label && <div style={{ fontSize: 11, color: '#4ade80', fontFamily: "'DM Mono','Courier New',monospace" }}>{i.variant_label}</div>}</div>
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>Hours</span>
                          <input type="number" min="0" step="0.1" placeholder="—" inputMode="decimal" value={hourMeters[i.variant_id] || ''} onChange={e => setHourMeters(prev => ({ ...prev, [i.variant_id]: e.target.value }))} disabled={locked}
                            style={{ width: 96, padding: '10px 8px', background: '#252838', border: '1px solid #2a2d3e', borderRadius: 6, color: '#e2e8f0', fontSize: 14, textAlign: 'center', boxSizing: 'border-box' }} />
                        </div>
                      ))}
                    </div>
                  ) : <div style={{ fontSize: 12, color: '#94a3b8' }}>No serialized units on this order.</div>}

                  {(order.rental_order_items || []).length > 0 && (
                    <div style={{ marginTop: 14, borderTop: '1px solid #1a1d2e', paddingTop: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showSpecUpdate ? 12 : 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: .5 }}>📦 Update item specs on return</div>
                        <button type="button" onClick={() => setShowSpecUpdate(p => !p)} disabled={locked} style={{ background: 'none', border: '1px solid #2a3050', borderRadius: 6, color: '#a78bfa', cursor: 'pointer', fontSize: 11, padding: '4px 10px' }}>{showSpecUpdate ? 'Hide' : 'Update'}</button>
                      </div>
                      {showSpecUpdate && (order.rental_order_items || []).map(orderItem => {
                        const schema = getItemSchema(orderItem);
                        if (!schema.length) return null;
                        const vals = specUpdates[orderItem.rental_item_id] || {};
                        return (
                          <div key={orderItem.rental_item_id} style={{ background: '#181928', border: '1px solid #1a1d2e', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 }}>{orderItem.item_name}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 8 }}>
                              {schema.map(field => {
                                const key = field.key || field.field_key, label = field.label || field.field_label, type = field.type || field.field_type, opts = field.options || [];
                                const sInp = { width: '100%', background: '#1e2235', border: '1px solid #2a3050', borderRadius: 6, padding: '8px', color: '#e2e8f0', fontSize: 12, outline: 'none', boxSizing: 'border-box' };
                                return (
                                  <div key={key}>
                                    <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 3, fontWeight: 600 }}>{label}</div>
                                    {type === 'select' && opts.length > 0
                                      ? <select style={sInp} value={vals[key] || ''} onChange={e => setSpecVal(orderItem.rental_item_id, key, e.target.value)} disabled={locked}><option value="">— {label} —</option>{opts.map(o => <option key={o} value={o}>{o}</option>)}</select>
                                      : <input type={type === 'number' ? 'number' : 'text'} style={sInp} value={vals[key] || ''} onChange={e => setSpecVal(orderItem.rental_item_id, key, e.target.value)} placeholder={label} disabled={locked} />}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ))}
            </div>
          )}

          {/* The close-out card stays live after Finish (Collect / Refund); everything above it is disabled then */}
          {closeoutCard()}
        </div>
      </div>

      {err && <div style={{ marginTop: 12, color: '#f87171', fontSize: 12 }} data-testid="dialog-error">{err}</div>}

      {/* Footer: what the server will do, and the one button. Sticky on a desk, fixed on a phone. */}
      <div data-testid="return-footer" style={narrow
        ? { position: 'fixed', left: 0, right: 0, bottom: 0, padding: '10px 12px calc(10px + env(safe-area-inset-bottom))', background: 'rgba(30,34,53,0.97)', borderTop: '1px solid #2a3050', zIndex: 1500, display: 'flex', flexDirection: 'column', gap: 8 }
        : { position: 'sticky', bottom: 0, marginTop: 'auto', padding: '14px 0 16px', background: '#1e2235', borderTop: '1px solid #2a3050', display: 'flex', alignItems: 'center', gap: 20, zIndex: 5 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {!narrow && !done && (na.effects || []).map((line, i) => <div key={i} style={{ ...UI.effect, padding: '1px 0' }}>• {line}</div>)}
          {!narrow && done && <div style={{ ...UI.effect, padding: '1px 0' }}>Returned · the strip on the order shows who and when</div>}
        </div>
        {done
          ? <button type="button" data-testid="closeout-done" onClick={() => onDone(done.order)} style={{ ...UI.goBtn(true, '#2e3150', '#e2e8f0'), flex: 'none', width: narrow ? '100%' : 'auto', minWidth: 280, padding: '14px 24px', fontSize: 15 }}>Done → order</button>
          : <button type="button" data-testid="return-finish" disabled={!canFinish} onClick={handleReturn} title={finishReason || ''}
              style={{ ...UI.goBtn(canFinish, sel?.bg, sel?.color), flex: 'none', width: narrow ? '100%' : 'auto', minWidth: 280, padding: '14px 24px', fontSize: 15 }}>
              {uploading ? '📷 Uploading…' : saving ? 'Saving…' : finishReason || 'Return & close order'}
            </button>}
      </div>

      {/* Full-size photo (tap the preview) */}
      {photoFull && photoPreview && (
        <div data-testid="damage-photo-full" onClick={() => setPhotoFull(false)} style={{ position: 'fixed', inset: 0, zIndex: 4500, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 12 }}>
          <img src={photoPreview} alt="damage, full size" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      )}
    </div>
  );
}
